import { AnimatePresence, motion } from "framer-motion"
import { PropsWithChildren, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"

interface AddedProductState {
    show: boolean
    skus: string[]
    addedProduct: (sku: string | string[]) => void
}

const AddedProductContext = createContext<AddedProductState>({
    skus: [],
    show: false,
    addedProduct: () => {},
})

export const AddedProductProvider = ({ children, closeTime }: PropsWithChildren<{ closeTime: number }>) => {
    const [skus, setSkus] = useState<AddedProductState["skus"]>([])
    const timer = useRef<NodeJS.Timeout>()
    useEffect(() => {
        if (skus.length) {
            timer.current = setTimeout(() => {
                setSkus([])
            }, closeTime)
            return () => {
                if (timer.current) clearTimeout(timer.current)
            }
        }
    }, [closeTime, skus])
    const addedProduct = useCallback((sku: string | string[]) => {
        setSkus((v) => [...v, ...(Array.isArray(sku) ? sku : [sku])])
    }, [])
    const value = useMemo(() => ({ show: skus.length > 0, skus, addedProduct }), [addedProduct, skus])
    return <AddedProductContext.Provider value={value}>{children}</AddedProductContext.Provider>
}

export const useAddedProduct = () => useContext(AddedProductContext)!

export const AddedProductPopupAnimation = ({ children, className }: PropsWithChildren<{ className?: string }>) => {
    const { show } = useAddedProduct()
    return (
        <AnimatePresence>
            {show ? (
                <motion.div
                    key="addedProduct"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3, ease: [0.55, 0.055, 0.675, 0.19] }}
                    className={className}>
                    {children}
                </motion.div>
            ) : null}
        </AnimatePresence>
    )
}
